import Typography from '@mui/material/Typography';
import BaseLayout from '../components/Layout/BaseLayout';
import EditableAnagraphicTable from '../components/Table/EditableAnagraphicTable';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import {
  Alert,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGetClient, useClientDateStatus } from '../api/ClientsApi';
import { useRefreshReports } from '../api/ReportsAPI';
import { useDispatch, useSelector } from 'react-redux';
import BaseTable, { BaseTableColumnProps } from '../components/Table/BaseTable';
import { useNavigate } from 'react-router-dom';
import { usePutData } from '../api/ApiManager';
import LoadingButton from '@mui/lab/LoadingButton';
import { ENDPOINTS } from '../api/EndPoints';
import { Dict } from '../schema/general';
import { resetTableRows } from '../actions/anagraphicTableActions';

type NotificationState = {
  type: 'success' | 'error';
  message: string;
} | null;

const EditClientAnagraphy = () => {
  const queryParameters = new URLSearchParams(window.location.search);
  const preSelectedClientId = queryParameters.get('client_cod');
  const [clientId] = useState<string>(preSelectedClientId ?? '');
  const { clientName, isPending, errorMessage } = useGetClient(clientId);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [notification, setNotification] = useState<NotificationState>(null);

  const { dateStatus, refetchDateStatus } = useClientDateStatus(clientId);
  const { startRefresh, isRefreshing, refreshStatus, refreshError, resetRefreshStatus } =
    useRefreshReports(clientId);

  const navigate = useNavigate();

  const rowsSelector: Dict[] = useSelector((state: any) => state.rows);

  const modalColumns: BaseTableColumnProps[] = Array.from(
    new Set(rowsSelector.flatMap((obj: any) => Object.keys(obj)))
  ).map((key) => ({ name: key as string }));

  const handleModalOpen = () => {
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setOpenModal(false);
  };
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(resetTableRows());
  }, [dispatch]);

  useEffect(() => {
    if (refreshStatus === 'completed') {
      setNotification({ type: 'success', message: 'Aggiornamento completato' });
      refetchDateStatus();
      resetRefreshStatus();
    } else if (refreshStatus === 'error') {
      setNotification({
        type: 'error',
        message: refreshError
          ? `Errore durante l'aggiornamento: ${refreshError}`
          : "Errore durante l'aggiornamento",
      });
      resetRefreshStatus();
    }
  }, [refreshStatus, refreshError, refetchDateStatus, resetRefreshStatus]);

  const Modal: React.FC = () => {
    const [modalConfirm, setModalConfirm] = useState<boolean>(false);
    const {
      statusCode,
      message,
      isPending: putPending,
    } = usePutData(
      ENDPOINTS.PUT_ANAGRAPHIC_REPORT(clientId),
      rowsSelector,
      modalConfirm
    );
    const handleModalConfirm = () => {
      setModalConfirm(true);
    };

    const handleMessageOnClose = () => {
      if (statusCode === 201) {
        dispatch(resetTableRows());
        navigate('/');
      }
    };

    return (
      <div>
        <Dialog
          open={openModal}
          onClose={handleModalClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="alert-dialog-title">
            Sei sicuro di voler continuare?
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              id="alert-dialog-description"
              className="flex flex-col space-y-6"
            >
              <Typography component={'span'} variant={'body1'}>
                Di seguito un riepilogo delle modifiche effettuate
              </Typography>
              <BaseTable columns={modalColumns} rows={rowsSelector} />
              <Typography component={'span'} variant={'body1'}>
                Clicca conferma per confermare le modifiche
              </Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Annulla</Button>
            <LoadingButton
              onClick={handleModalConfirm}
              loading={putPending}
              autoFocus
            >
              Conferma
            </LoadingButton>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={!putPending && message !== undefined}
          onClose={handleMessageOnClose}
          autoHideDuration={2000}
          message={message}
        />
      </div>
    );
  };

  return (
    <BaseLayout isLoading={isPending} errorMessage={errorMessage}>
      <div className="flex flex-col w-full justify-center">
        <div className="my-10 mx-auto text-center">
          <Typography fontSize="2.5rem" fontWeight={700}>
            {clientName}
          </Typography>
          {dateStatus && (
            <div className="flex flex-row items-center justify-center space-x-3 mt-3">
              <Typography variant="body1" component="span" color="text.secondary">
                Ultimo aggiornamento:
              </Typography>
              <Typography
                variant="body1"
                component="span"
                fontWeight={600}
                color={dateStatus.is_aligned ? 'success.main' : 'error.main'}
              >
                {dateStatus.current_date}
              </Typography>
              {!dateStatus.is_aligned && dateStatus.last_available_date && (
                <Typography variant="body2" component="span" color="text.secondary">
                  Disponibile: {dateStatus.last_available_date}
                </Typography>
              )}
              <LoadingButton
                variant="outlined"
                size="small"
                onClick={startRefresh}
                disabled={dateStatus.is_aligned || isRefreshing}
                loading={isRefreshing}
                loadingPosition="start"
                startIcon={<RefreshIcon />}
              >
                {isRefreshing ? 'Aggiornamento in corso...' : 'Aggiorna'}
              </LoadingButton>
              {isRefreshing && (
                <div className="flex items-center space-x-1">
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Aggiornamento in corso...
                  </Typography>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="mx-10 mb-10 px-10 py-4 bg-sky-100 rounded-lg">
          <div className="mb-4">
            <Typography fontSize="1.2rem" fontWeight={600}>
              Modifica dati anagrafici
            </Typography>
          </div>
          <div className="flex flex-col space-y-2">
            <EditableAnagraphicTable
              clientId={clientId}
              classNames="max-h-[75vh] !rounded-lg"
            />
            <div className="flex flex-row w-full justify-end space-x-4">
              <Button
                component={Link}
                to="/"
                className="mr-2"
                variant="outlined"
              >
                Annulla
              </Button>
              <Button
                className="mr-2"
                variant="contained"
                onClick={handleModalOpen}
                disabled={rowsSelector.length === 0}
              >
                Applica modifiche
              </Button>
            </div>
          </div>
        </div>
        <Modal />
        <Snackbar
          open={notification !== null}
          autoHideDuration={5000}
          onClose={() => setNotification(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={notification?.type ?? 'info'}
            onClose={() => setNotification(null)}
            variant="filled"
          >
            {notification?.message}
          </Alert>
        </Snackbar>
      </div>
    </BaseLayout>
  );
};
export default EditClientAnagraphy;
